// lib/utils/cookies.ts
import Cookies from 'js-cookie';

const TOKEN_KEY = 'auth_token_frontend';
const USER_KEY  = 'auth_user_frontend';

export const LANG_KEY  = 'ui_lang';
export const THEME_KEY = 'ui_theme';

export interface StoredUser {
    _id: string;
    name: string;
    email: string;
}

function isStoredUser(value: unknown): value is StoredUser {
    if (!value || typeof value !== 'object') return false;
    const v = value as Record<string, unknown>;
    return (
        typeof v._id === 'string' &&
        typeof v.name === 'string' &&
        typeof v.email === 'string'
    );
}

export const cookieUtils = {
    // Token
    setToken: (token: string, expiresInDays: number = 30) => {
        Cookies.set(TOKEN_KEY, token, {
            expires: expiresInDays,
            secure: true,
            sameSite: 'strict',
            path: '/',
        });
    },

    getToken: (): string | undefined => {
        return Cookies.get(TOKEN_KEY);
    },

    removeToken: () => {
        Cookies.remove(TOKEN_KEY, { path: '/' });
    },

    // User data — only stores non-sensitive identity fields
    setUser: (user: StoredUser, expiresInDays: number = 30) => {
        const safe: StoredUser = {
            _id:   user._id,
            name:  user.name,
            email: user.email,
        };
        Cookies.set(USER_KEY, JSON.stringify(safe), {
            expires: expiresInDays,
            secure: true,
            sameSite: 'strict',
            path: '/',
        });
    },

    getUser: (): StoredUser | null => {
        const raw = Cookies.get(USER_KEY);
        if (!raw) return null;
        try {
            const parsed: unknown = JSON.parse(raw);
            return isStoredUser(parsed) ? parsed : null;
        } catch {
            return null;
        }
    },

    removeUser: () => {
        Cookies.remove(USER_KEY, { path: '/' });
    },

    // Clear all auth cookies
    clearAuth: () => {
        Cookies.remove(TOKEN_KEY, { path: '/' });
        Cookies.remove(USER_KEY, { path: '/' });
    },

    // UI preferences
    setLang: (lang: 'en' | 'ar') => {
        Cookies.set(LANG_KEY, lang, { expires: 365, sameSite: 'lax', path: '/' });
    },
    getLang: (): 'en' | 'ar' | undefined => {
        const v = Cookies.get(LANG_KEY);
        return v === 'ar' || v === 'en' ? v : undefined;
    },
    setTheme: (theme: 'light' | 'dark' | 'system') => {
        Cookies.set(THEME_KEY, theme, { expires: 365, sameSite: 'lax', path: '/' });
    },
    getTheme: (): 'light' | 'dark' | 'system' | undefined => {
        const v = Cookies.get(THEME_KEY);
        return v === 'light' || v === 'dark' || v === 'system' ? v : undefined;
    },
};
